import {
  addToCart,
  addCartItems,
  getCartData,
  updateItemQuantities,
  removeItemsFromCart,
  applyCouponHandler,
  removeCouponsHandler,
} from '~/utils/cart';
import { useNotificationStore } from './notification';
import { defineStore } from '#imports';
import { getCartMutationHandler } from '~/hooks/mutations/get-cart-mutation-handler.js';
import { fetchFreePickCandidates } from '~/hooks/queries/use-cart-data-query.js';

const getFreePickErrorMessage = (error, fallback: string) => {
  if (error instanceof Error) return error.message;
  if (Array.isArray(error) && error[0]?.message) return error[0].message;
  return fallback;
};

export const useCartStore = defineStore('cart', {
  state: () => ({
      cart: undefined,
      isMiniCartOpen: false,
      isLoading: false,
      isLoadingFreePickCandidates: false,
      addingToCart: [],
      requestQueue: [],
      isProcessingQueue: false,
      removingKeys: [],
  }),
  getters: {
    isCartEmpty() {
      if (! this.getCurrentCart ||! this.getCurrentCartItems) {
        return true
      }

      return this.getCurrentCart.isEmpty
    },
    getCurrentCart(state) {
      return state.cart
    },
    getCurrentCartItems(state): array {
      if (!state.cart) {
        return []
      }

      return state.cart?.contents?.nodes ?? []
    },
    getCartProductIds(state) {
      const currentCartItems = this.getCurrentCartItems;
      if (!currentCartItems) {
        return []
      }

      const cleanProducts = currentCartItems.filter((item) => item.product && !item.isFreePick);

      return cleanProducts.map((cartItem) => cartItem.product.node.productId)
    },
    getCartVariationIds (): number[] {
      const response = [];

      this.getCurrentCartItems.forEach((item) => {
        if (item.variation) {
          response.push(item.variation.node.databaseId);
        }
      })

      return response;
    },
    getAllCartItemKeys(state) {
      if (! this.getCurrentCartItems) {
        return []
      }

      return this.getCurrentCartItems.filter((cartItem) => !cartItem.isFreePick).map((cartItem) => cartItem.key)
    },
    // if all cartItems have the same quantity, return value else false.
    getCartQuantityMatchValue() {
      if (! this.getCurrentCartItems) {
        return false
      }

      let firstLoop = true
      let quantity

      this.getCurrentCartItems.filter((cartItem) => !cartItem.isFreePick).forEach((cartItem) => {
        if (firstLoop) {
          quantity = cartItem.quantity
          firstLoop = false
        } else if (quantity !== cartItem.quantity) {
          quantity = false
        }
      })

      return quantity
    },
    isRemoving(): boolean {
      // True only when a removal is in progress
      return this.removingKeys && this.removingKeys.length > 0;
    }
  },
  actions: {
    toggleMiniCart() {
      this.isMiniCartOpen = ! this.isMiniCartOpen
    },
    closeMiniCart () {
      this.isMiniCartOpen = false;
    },
    setCart(cart) {
      this.cart = cart
    },
    async addToCart(productId, quantity = 1, subscriptionScheme = '0') {
      return new Promise((resolve) => {
        this.addingToCart.push(productId);

        if (!this.requestQueue) {
          this.requestQueue = [];
        }

        this.requestQueue.push({ productId, quantity, subscriptionScheme, resolve });

        if (!this.isProcessingQueue) {
          this.processQueue();
        }
      })
    },
    async processQueue() {
      if (this.requestQueue.length === 0 || this.isProcessingQueue) {
        return;
      }

      this.isProcessingQueue = true;

      const { productId, quantity, subscriptionScheme, resolve } = this.requestQueue.shift();

      this.isLoading = true;

      try {
        const { data } = await addToCart({ productId, quantity, subscriptionScheme });

        if (data?.addToCart?.cart) {
          this.setCart(data.addToCart.cart)
        }

        resolve({ success: true, data });
      } catch(error) {
        console.error('Error adding to cart: ', error)
        resolve({ success: false, error });
      } finally {
        this.isLoading = false;
        this.addingToCart = this.addingToCart.filter((addingProductId) => addingProductId !== productId);

        this.isProcessingQueue = false;

        if (this.requestQueue.length > 0) {
          this.processQueue();
        }
      }
    },
    async addCartItems(productIds: Array<Number>) {
      this.isLoading = true;
      const { data } = await addCartItems(productIds)
      const notificationStore = useNotificationStore()

      if (data?.addCartItems?.cart) {
        this.setCart(data.addCartItems.cart)

        notificationStore.setNotification({
          messageText: 'Cart Updated',
          messageType: 'success',
        })
      }

      this.isLoading = false;
    },
    async removeItemsFromCart(cartItemKeys = [], removeAll = false) {
      // Track per-item removing state and set a global removal lock
      const keysToRemove = cartItemKeys.map((k) => String(k));
      try {
        // merge keys to avoid duplicates
        const asSet = new Set([...(this.removingKeys ?? []).map(String), ...keysToRemove]);
        this.removingKeys = Array.from(asSet);

        this.isLoading = true;
        const { data } = await removeItemsFromCart({ cartItemKeys, removeAll })

        if (data?.removeItemsFromCart?.cart) {
          this.setCart(data.removeItemsFromCart.cart)
        }
      } finally {
        // Clear per-item removing flags for the provided keys
        const clearSet = new Set(keysToRemove);
        this.removingKeys = (this.removingKeys ?? []).filter((k) => !clearSet.has(String(k)));
        this.isLoading = false;
      }
    },
    async setCartFromSession() {
      await this.getCartData()
    },
    async refreshCart() {
      await this.getCartData()
    },
    async getCartData() {
      this.isLoading = true;
      const cart = await getCartData()

      this.setCart(cart)

      // check if any system notifications to show
      if (cart?.systemNotices?.length) {
        const notificationStore = useNotificationStore()

        // @todo update system notifications to handle array
        notificationStore.setNotification({
          messageText: cart.systemNotices[0].message,
          messageType: cart.systemNotices[0].noticeType,
        })
      }

      this.isLoading = false;
    },
    async updateItemQuantities(cartItemQuantityInput) {
      this.isLoading = true;

      // Validate and enforce soldIndividually limits
      const validatedInput = cartItemQuantityInput.map(item => {
        // Find the cart item to check soldIndividually setting
        const cartItem = this.getCurrentCartItems.find(ci => ci.key === item.key);
        if (cartItem?.isFreePick) {
          return { ...item, quantity: 1 };
        }

        // Check if product has soldIndividually set to true
        const soldIndividually = cartItem?.product?.node?.soldIndividually;

        // If soldIndividually is true and quantity > 1, force to 1
        if (soldIndividually && item.quantity > 1) {
          return {
            ...item,
            quantity: 1
          };
        }

        return item;
      });

      const { data } = await updateItemQuantities({ cartItemQuantityInput: validatedInput })

      if (data?.updateItemQuantities?.cart) {
        this.setCart(data.updateItemQuantities.cart)
      }

      this.isLoading = false;
    },
    async selectFreePick(productId: number) {
      const { mutationHandler } = getCartMutationHandler('selectFreePick');
      if (!mutationHandler) throw new Error('Free Pick selection is unavailable.');

      this.isLoading = true;
      try {
        const { data } = await mutationHandler({ productId });
        const cart = data?.selectFreePick?.cart;
        if (!cart) throw new Error('The selected Free Pick could not be confirmed. Refresh your basket and try again.');
        this.setCart(cart);
        return cart;
      } catch (error) {
        throw new Error(getFreePickErrorMessage(error, 'Could not select this free library. Refresh your basket and try again.'));
      } finally {
        this.isLoading = false;
      }
    },
    async loadMoreFreePickCandidates() {
      const freePick = this.getCurrentCart?.freePick;
      const candidates = freePick?.candidates;
      if (!candidates?.hasMore || this.isLoadingFreePickCandidates) return;

      this.isLoadingFreePickCandidates = true;
      try {
        const nextPage = await fetchFreePickCandidates(24, candidates.nextOffset);
        if (!nextPage) throw new Error('Could not load more free libraries. Please try again.');

        const currentFreePick = this.getCurrentCart?.freePick;
        if (currentFreePick !== freePick) {
          return;
        }
        const currentCandidates = currentFreePick.candidates;
        const seenIds = new Set(currentCandidates.nodes.map((product) => product.productId));
        const nodes = [
          ...currentCandidates.nodes,
          ...nextPage.nodes.filter((product) => {
            if (seenIds.has(product.productId)) return false;
            seenIds.add(product.productId);
            return true;
          }),
        ];
        this.setCart({
          ...this.getCurrentCart,
          freePick: {
            ...currentFreePick,
            candidates: { ...nextPage, nodes },
          },
        });
      } catch (error) {
        throw new Error(getFreePickErrorMessage(error, 'Could not load more free libraries. Please try again.'));
      } finally {
        this.isLoadingFreePickCandidates = false;
      }
    },
    async clearFreePick() {
      const { mutationHandler } = getCartMutationHandler('clearFreePick');
      if (!mutationHandler) throw new Error('Free Pick removal is unavailable.');

      this.isLoading = true;
      try {
        const { data } = await mutationHandler({});
        const cart = data?.clearFreePick?.cart;
        if (!cart) throw new Error('The Free Pick could not be removed. Refresh your basket and try again.');
        this.setCart(cart);
        return cart;
      } catch (error) {
        throw new Error(getFreePickErrorMessage(error, 'Could not remove the free library. Refresh your basket and try again.'));
      } finally {
        this.isLoading = false;
      }
    },
    clearCart() {
      this.removeItemsFromCart([], true);

      this.setCart(undefined)

      return this.cart
    },
    async applyCoupon(code) {
      const result = await applyCouponHandler({ code })

      if (result.error) {
        return result
      }

      // check for success
      if (result.applied) {
        if (result.cart) {
          this.setCart(result.cart)
        }

        return result
      }

      return {
        error: true,
        message: 'There was an error. Please try again or contact support.'
      }
    },
    async removeCoupons(couponCodes) {
      const result = await removeCouponsHandler({ couponCodes })

      if (result.errors) {
        return {
          error: true,
          message: 'There was an error. Please try again or contact support.'
        }
      }

      // check for success
      if (result?.removeCoupons?.cart) {
        this.setCart(result.removeCoupons.cart)

        return true
      }

      return {
        error: true,
        message: 'There was an error. Please try again or contact support.'
      }
    }
  },
});
